import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Button, Modal } from 'react-bootstrap';
import { 
  FaShieldAlt, 
  FaBolt, 
  FaLeaf, 
  FaMapMarkerAlt,
  FaMicrochip,
  FaCheckCircle,
  FaServer,
  FaLightbulb,
  FaUsers,
  FaArrowRight,
  FaCube,
  FaNetworkWired,
  FaCloud,
  FaBrain,
  FaCode
} from 'react-icons/fa';
import EditableText from '../components/EditableText';

const gpus = [
  {
    id: 'h200',
    name: 'NVIDIA H200',
    status: 'available',
    statusLabel: 'Available Now',
    description: 'Next-gen AI training and inference with 141GB HBM3e memory',
    specs: {
      memory: '141GB HBM3e',
      performance: '4.8 TB/s memory bandwidth',
      use_cases: ['LLM training', 'Inference', 'Fine-tuning', 'RAG']
    }
  },
  {
    id: 'gb300',
    name: 'NVIDIA GB300',
    status: 'coming',
    statusLabel: 'Coming Soon',
    description: 'Blackwell architecture for massive-scale AI with advanced NVLink',
    specs: {
      memory: '192GB HBM3e',
      performance: '15 TB/s memory bandwidth',
      use_cases: ['Trillion parameter models', 'Real-time inference']
    }
  },
  {
    id: 'b300',
    name: 'NVIDIA B300',
    status: 'coming',
    statusLabel: 'Coming Soon',
    description: 'Next-generation Blackwell for unprecedented AI performance',
    specs: {
      memory: '288GB HBM3e',
      performance: 'Ultra-low latency',
      use_cases: ['Enterprise AI', 'Autonomous systems']
    }
  }
];

const services = [
  {
    id: 'gpu-rental',
    title: 'GPU Rental',
    description: 'Access powerful NVIDIA H200 and Blackwell GPUs for your AI workloads',
    icon: <FaMicrochip />,
    features: ['Single GPU to multi-node clusters', 'High-performance networking']
  },
  {
    id: 'kubernetes',
    title: 'Kubernetes as a Service',
    description: 'Fully managed Kubernetes clusters with GPU scheduling and auto-scaling',
    icon: <FaCube />,
    features: ['GPU-aware scheduling', 'Auto-scaling', 'Integrated monitoring', 'Enterprise SLAs']
  },
  {
    id: 'baremetal',
    title: 'Bare Metal SXM',
    description: 'Dedicated NVIDIA SXM form factor servers for maximum performance',
    icon: <FaServer />,
    features: ['NVIDIA HGX/HBM integration', 'Dedicated resources', 'Custom configurations', 'Ultra-low latency']
  },
  {
    id: 'infiniband',
    title: 'InfiniBand Rail Topology',
    description: 'High-speed interconnect fabric for distributed AI training',
    icon: <FaNetworkWired />,
    features: ['400Gb/s HDR InfiniBand', 'Rail-optimized topology', 'NVLink support', 'MPI acceleration']
  },
  {
    id: 'iaac',
    title: 'IaC Support',
    description: 'Infrastructure as Code with support for automated provisioning',
    icon: <FaCode />,
    features: ['Infrastructure templates', 'Version control', 'Automated provisioning']
  },
  {
    id: 'llm-ondemand',
    title: 'LLM on Demand',
    description: 'Ready-to-deploy Large Language Models with API access',
    icon: <FaBrain />,
    features: ['Open-source models', 'Fine-tuning support', 'API endpoints']
  },
  {
    id: 'use-case-dev',
    title: 'Use Case Development Kit',
    description: 'Leverage NVIDIA SDKs, Blueprints, and pre-trained GenAI models',
    icon: <FaLightbulb />,
    features: ['NVIDIA Blueprints', 'Pre-trained models', 'Development frameworks']
  },
  {
    id: 'ai-solutions',
    title: 'AI Solutions',
    description: 'Turnkey AI applications for immediate business deployment',
    icon: <FaCloud />,
    features: ['Ready-to-deploy AI apps', 'Industry-specific solutions', 'Custom development']
  },
  {
    id: 'advisory',
    title: 'Advisory Services',
    description: 'Expert guidance for implementing AI use cases in your business',
    icon: <FaUsers />,
    features: ['AI strategy consulting', 'Implementation support', 'Operational optimization']
  }
];

function Home() {
  const [showModal, setShowModal] = useState(false);
  const [content, setContent] = useState({});

  useEffect(() => {
    fetch('/api/content?page=home')
      .then(res => res.json())
      .then(data => setContent(data))
      .catch(() => {});
  }, []);

  return (
    <>
      <Helmet>
        <title>6G AI Sweden | Your AI Factory in Sweden</title>
        <meta name="description" content="World-class AI cloud infrastructure powered by NVIDIA H200 GPUs. Sovereign, sustainable AI compute in Stockholm, Sweden. GDPR compliant, ISO 27001 certified." />
        <meta name="keywords" content="AI cloud, GPU rental, NVIDIA H200, Sweden AI, data sovereignty, AI infrastructure, machine learning" />
        <meta property="og:title" content="6G AI Sweden | Your AI Factory in Sweden" />
        <meta property="og:description" content="World-class AI cloud infrastructure powered by NVIDIA. Sovereign and sustainable AI compute in Stockholm, Sweden." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "6G AI Sweden",
            "description": "AI cloud provider in Sweden",
            "url": "https://6gai.se",
            "areaServed": "Sweden",
            "serviceType": "AI cloud infrastructure"
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="hero-section">
        <div style={{
          background: 'rgba(12, 13, 13, 0.4)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderRadius: '12px',
          padding: '3rem 5%',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          margin: '100px 2% 40px',
          width: '96%'
        }}>
          <Container className="hero-content" style={{ position: 'relative', zIndex: 1 }}>
            <Row className="align-items-center">
              <Col lg={8}>
                <div className="hero-badge animate-in" style={{ 
                  background: 'rgba(123, 44, 191, 0.15)', 
                  border: '1px solid rgba(199, 125, 255, 0.3)',
                  boxShadow: '0 0 30px rgba(157, 78, 221, 0.2)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ 
                    width: '8px', 
                    height: '8px', 
                    borderRadius: '50%', 
                    background: '#00ff88',
                    boxShadow: '0 0 10px #00ff88',
                    animation: 'pulse 2s ease-in-out infinite',
                    display: 'inline-block',
                    flexShrink: 0
                  }} />
                  <span>Live Cluster Available</span>
                </div>
              <h1 className="hero-title animate-in delay-1" style={{
                fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
                background: 'linear-gradient(135deg, #ffffff 0%, #e5e7eb 30%, #ffffff 50%, #9d4edd 70%, #ffffff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginTop: '1.5rem'
              }}>
                <EditableText section="hero_title" page="home" defaultValue="Your AI Factory" /><br />in <span style={{ 
                  background: 'linear-gradient(135deg, #4a0080, #9d4edd, #c77dff)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>Sweden</span>
              </h1>
              <p className="hero-subtitle animate-in delay-2" style={{
                fontSize: '1.35rem',
                lineHeight: '1.8',
                color: 'rgba(255, 255, 255, 0.75)',
                textShadow: '0 0 30px rgba(157, 78, 221, 0.1)',
                marginTop: '1.5rem',
                maxWidth: '650px'
              }}>
                <EditableText section="hero_subtitle" page="home" defaultValue="Accelerate your innovations with cutting-edge AI cloud solutions, powered by NVIDIA's advanced AI chips — H200 GPUs with B300/GB300 coming soon. Your data stays sovereign under Swedish jurisdiction." />
              </p>
              <div className="hero-cta animate-in delay-3" style={{ gap: '1rem', marginTop: '2rem', display: 'flex', flexWrap: 'wrap' }}>
                <Button 
                  variant="primary" 
                  size="lg" 
                  as={Link} 
                  to="/contact"
                  style={{
                    background: 'linear-gradient(135deg, #4a0080, #9d4edd)',
                    border: 'none',
                    padding: '1rem 2rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    boxShadow: '0 0 30px rgba(157, 78, 221, 0.4), 0 10px 40px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  Request Access
                </Button>
                <Button 
                  variant="outline-light" 
                  size="lg" 
                  as={Link} 
                  to="/infrastructure"
                  style={{
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    padding: '1rem 2rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    background: 'rgba(255, 255, 255, 0.03)',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: '#fff',
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = 'rgba(157, 78, 221, 0.2)';
                    e.target.style.borderColor = 'rgba(199, 125, 255, 0.5)';
                    e.target.style.color = '#fff';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.03)';
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                    e.target.style.color = '#fff';
                  }}
                >
                  Explore Infrastructure <FaArrowRight />
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.2); }
          }
        `}</style>
      </section>

      {/* Trust Badges */}
      <section className="trust-section">
        <Container>
          <div className="trust-grid">
            <div className="trust-item" onClick={() => setShowModal(true)} style={{ cursor: 'pointer' }}>
              <FaShieldAlt size={24} />
              <span><EditableText section="trust_iso" page="home" defaultValue="ISO 27001 Certified" as="span" /></span>
            </div>
            <div className="trust-item">
              <FaShieldAlt size={24} />
              <span><EditableText section="trust_gdpr" page="home" defaultValue="GDPR Compliant" as="span" /></span>
            </div>
            <div className="trust-item">
              <FaShieldAlt size={24} />
              <span><EditableText section="trust_ai_act" page="home" defaultValue="EU AI Act Ready" as="span" /></span>
            </div>
            <div className="trust-item">
              <FaMapMarkerAlt size={24} />
              <span><EditableText section="trust_location" page="home" defaultValue="Stockholm, Sweden" as="span" /></span>
            </div>
          </div>
        </Container>
      </section>

      {/* ISO 27001 Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg"
        style={{
          background: 'rgba(0, 0, 0, 0.8)',
        }}
      >
        <Modal.Header 
          closeButton 
          closeVariant="white"
          style={{ 
            background: 'linear-gradient(145deg, rgba(14, 14, 20, 0.95), rgba(14, 14, 20, 0.9))', 
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#fff'
          }}
        >
          <Modal.Title style={{ fontWeight: 700, color: '#fff' }}>ISO/IEC 27001:2022 Certification</Modal.Title>
        </Modal.Header>
        <Modal.Body 
          style={{ 
            background: 'linear-gradient(145deg, rgba(14, 14, 20, 0.95), rgba(14, 14, 20, 0.9))', 
            color: '#fff',
            padding: '2rem'
          }}
        >
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div style={{
              width: '100px',
              height: '100px',
              background: 'linear-gradient(135deg, #4a0080, #9d4edd)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 2rem',
              boxShadow: '0 0 40px rgba(157, 78, 221, 0.4)'
            }}>
              <FaShieldAlt size={40} style={{ color: '#fff' }} />
            </div>
            <h4 style={{ marginBottom: '1rem', color: '#fff' }}>6G AI Sweden AB</h4>
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '2rem' }}>
              Certificate Number: UUQDSTSI20260130SWEIS10K
            </p>
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '2rem' }}>
              This certification demonstrates our commitment to information security management systems (ISMS) and protects your data with the highest international standards.
            </p>
            <Button 
              variant="primary" 
              size="lg"
              href="https://www.iafcertsearch.org/certification/trMbZbhRyfsa9168BxtLGYw2"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: 'linear-gradient(135deg, #4a0080, #9d4edd)',
                border: 'none',
                padding: '1rem 2rem',
              }}
            >
              View Certificate
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Value Propositions */}
      <section className="section">
        <Container>
          <div className="text-center mb-5">
            <h2 className="section-title"><EditableText section="why_choose_title" page="home" defaultValue="Why Choose 6G AI Sweden" as="span" /></h2>
            <p className="section-subtitle"><EditableText section="why_choose_subtitle" page="home" defaultValue="Sovereign, sustainable, and cutting-edge AI infrastructure" as="span" /></p>
          </div>
          <Row>
            <Col md={6} lg={3} className="mb-4">
              <div className="value-card interactive-card gradient-border">
                <div className="value-icon">
                  <FaShieldAlt />
                </div>
                <h4 className="value-title"><EditableText section="value_1_title" page="home" defaultValue="Sovereign AI Cloud" as="span" /></h4>
                <p className="value-text">
                  <EditableText section="value_1_text" page="home" defaultValue="Complete data control under Swedish jurisdiction. Full GDPR and EU AI Act compliance for sensitive sectors." as="span" />
                </p>
              </div>
            </Col>
            <Col md={6} lg={3} className="mb-4">
              <div className="value-card interactive-card gradient-border">
                <div className="value-icon">
                  <FaMicrochip />
                </div>
                <h4 className="value-title"><EditableText section="value_2_title" page="home" defaultValue="NVIDIA Technology" as="span" /></h4>
                <p className="value-text">
                  <EditableText section="value_2_text" page="home" defaultValue="Access to the latest NVIDIA AI chips including H200 and upcoming B300/GB300 with Blueprints and pre-trained models." as="span" />
                </p>
              </div>
            </Col>
            <Col md={6} lg={3} className="mb-4">
              <div className="value-card interactive-card gradient-border">
                <div className="value-icon">
                  <FaLeaf />
                </div>
                <h4 className="value-title"><EditableText section="value_3_title" page="home" defaultValue="Sustainable" as="span" /></h4>
                <p className="value-text">
                  <EditableText section="value_3_text" page="home" defaultValue="100% renewable energy powered facility with heat recovery system returning surplus heat to Stockholm's district heating." as="span" />
                </p>
              </div>
            </Col>
            <Col md={6} lg={3} className="mb-4">
              <div className="value-card interactive-card gradient-border">
                <div className="value-icon">
                  <FaMapMarkerAlt />
                </div>
                <h4 className="value-title"><EditableText section="value_4_title" page="home" defaultValue="Stockholm Kista" as="span" /></h4>
                <p className="value-text">
                  <EditableText section="value_4_text" page="home" defaultValue="Located in atNorth Data Center, Stockholm's tech hub. Low-latency connections with leading research institutions." as="span" />
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* GPU Section */}
      <section className="section section-dark">
        <Container>
          <div className="text-center mb-5">
            <h2 className="section-title"><EditableText section="gpus_title" page="home" defaultValue="NVIDIA GPU Infrastructure" as="span" /></h2>
            <p className="section-subtitle"><EditableText section="gpus_subtitle" page="home" defaultValue="Cutting-edge accelerators for your AI workloads" as="span" /></p>
          </div>
          <Row>
            {gpus.map((gpu) => (
              <Col md={4} key={gpu.id} className="mb-4">
                <div className="gpu-card interactive-card gradient-border">
                  <span className={`gpu-badge ${gpu.status}`}>
                    {gpu.statusLabel}
                  </span>
                  <h3 className="gpu-name"><EditableText section={`gpu_${gpu.id}_name`} page="home" defaultValue={gpu.name} as="span" /></h3>
                  <p className="gpu-description"><EditableText section={`gpu_${gpu.id}_desc`} page="home" defaultValue={gpu.description} as="span" /></p>
                  <div className="gpu-specs">
                    <div className="gpu-spec">
                      <FaCheckCircle /> <EditableText section={`gpu_${gpu.id}_memory`} page="home" defaultValue={gpu.specs.memory} as="span" />
                    </div>
                    <div className="gpu-spec">
                      <FaCheckCircle /> <EditableText section={`gpu_${gpu.id}_perf`} page="home" defaultValue={gpu.specs.performance} as="span" />
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Services Section */}
      <section className="section">
        <Container>
          <div className="text-center mb-5">
            <h2 className="section-title"><EditableText section="services_title" page="home" defaultValue="Comprehensive AI Services" as="span" /></h2>
            <p className="section-subtitle"><EditableText section="services_subtitle" page="home" defaultValue="End-to-end solutions for your AI journey" as="span" /></p>
          </div>
          <Row>
            {services.map((service) => (
              <Col md={6} lg={3} key={service.id} className="mb-4">
                <div className="service-card interactive-card">
                  <div className="service-icon">{service.icon}</div>
                  <h4 className="service-title"><EditableText section={`service_${service.id}_title`} page="home" defaultValue={service.title} as="span" /></h4>
                  <p className="service-text"><EditableText section={`service_${service.id}_desc`} page="home" defaultValue={service.description} as="span" /></p>
                  <ul className="service-features">
                    {service.features.map((feature, idx) => (
                      <li key={idx}><EditableText section={`service_${service.id}_feature_${idx}`} page="home" defaultValue={feature} as="span" /></li>
                    ))}
                  </ul>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="section section-dark">
        <Container>
          <div className="cta-section">
            <h2 className="cta-title"><EditableText section="cta_title" page="home" defaultValue="Join Sweden's AI Revolution" as="span" /></h2>
            <p className="cta-text">
              <EditableText section="cta_text" page="home" defaultValue="Partner with us to accelerate your AI initiatives with sovereign, sustainable infrastructure." as="span" />
            </p>
            <Button variant="primary" size="lg" as={Link} to="/contact">
              <EditableText section="cta_button" page="home" defaultValue="Get in Touch" as="span" /> <FaArrowRight />
            </Button>
          </div>
        </Container>
      </section>
    </>
  );
}

export default Home;
